/**
 * **인플루언서 질문** — 파라미터를 보내고 도메인 타입을 받는다. 질문 9개가 여기 있다.
 *
 * ```
 *   actions/influencer.js     Firestore 몫 + 브라우저 후처리(라벨·재정렬)
 *     │ fetchInfluencerListPage(params) …
 *   이 파일                   질문 — op 이름과 결과 모양을 안다 (머리의 resolveMock*() 는 influencer-mock)
 *     │ els-request.ts        전송 (dedupe · 2초 캐시 · 트레이싱)
 *     ▼ POST /api/els/influencers   { op, params }
 *   server/influencer/        답한다
 * ```
 *
 * op마다 **결과를 구조적으로 확인하는 함수 하나**를 둔다 — HTTP를 건너온 `unknown`을
 * 타입 단언 없이 좁히기 위해서다(`actions/els/moment.ts`와 같은 규약).
 */

import type {
  InfluencerListRef,
  InfluencerFacetsRequest,
  InfluencerDocGetRequest,
  InfluencerFacetsResponse,
  InfluencerDocGetResponse,
  InfluencerFullListRequest,
  InfluencerListPageRequest,
  InfluencerListRefsRequest,
  InfluencerFullListResponse,
  InfluencerListPageResponse,
  InfluencerListRefsResponse,
  InfluencerDocsByIdsRequest,
  InfluencerLatestWeekRequest,
  InfluencerDocsByIdsResponse,
  InfluencerLatestWeekResponse,
  InfluencerMarkExcludedRequest,
  InfluencerMicroAccountsRequest,
  InfluencerWeekBreakdownRequest,
  InfluencerMarkExcludedResponse,
  InfluencerMicroAccountsResponse,
  InfluencerWeekBreakdownResponse,
} from 'src/types/influencer/influencer-ops';

import { createElsProxyRequest } from 'src/actions/els/els-request';
import {
  resolveMockInfluencerLatestWeek,
  resolveMockInfluencerWeekBreakdown,
} from 'src/actions/els/dashboard-mock';
import {
  resolveMockInfluencerDoc,
  resolveMockInfluencerFacets,
  resolveMockInfluencerListPage,
  resolveMockInfluencerListRefs,
  resolveMockInfluencerDocsByIds,
} from 'src/actions/els/influencer-mock';

/** 인덱스(자원)당 엔드포인트 하나 */
const INFLUENCER_OPS_PATH = '/api/els/influencers';

const requestInfluencerOp = createElsProxyRequest(INFLUENCER_OPS_PATH);

function assertOpObject(data: unknown, opLabel: string): object {
  if (data == null || typeof data !== 'object') {
    throw new Error(`[${opLabel}] 응답이 객체가 아니다`);
  }
  if ('error' in data) {
    throw new Error(`[${opLabel}] ${String(data.error)}`);
  }
  return data;
}

function readRows(data: object, opLabel: string): unknown[] {
  const rows = 'rows' in data && Array.isArray(data.rows) ? data.rows : null;
  if (!rows) throw new Error(`[${opLabel}] rows가 배열이 아니다`);
  return rows;
}

function readCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** 목록 한 페이지 (`list.page`) */
export async function fetchInfluencerListPage(
  params: InfluencerListPageRequest,
): Promise<InfluencerListPageResponse> {
  // mockup 갈래 — 잡히지 않으면 `null` 이라 아래로 그냥 흐른다(`influencer-mock` 머리말)
  const mocked = resolveMockInfluencerListPage(params);
  if (mocked) return mocked;

  const response = await requestInfluencerOp({ op: 'list.page', params });
  const data = assertOpObject(response.data, 'fetchInfluencerListPage');
  return {
    rows: readRows(data, 'fetchInfluencerListPage'),
    total: readCount('total' in data ? data.total : null),
  };
}

/** 참조 목록 (`list.refs`) — 관련순 재정렬은 호출부(브라우저)가 한다 */
export async function fetchInfluencerListRefs(
  params: InfluencerListRefsRequest,
): Promise<InfluencerListRefsResponse> {
  const mocked = resolveMockInfluencerListRefs(params);
  if (mocked) return mocked;

  const response = await requestInfluencerOp({ op: 'list.refs', params });
  const data = assertOpObject(response.data, 'fetchInfluencerListRefs');
  const rawRefs = 'refs' in data && Array.isArray(data.refs) ? data.refs : null;
  if (!rawRefs) throw new Error('[fetchInfluencerListRefs] refs가 배열이 아니다');

  const refs: InfluencerListRef[] = [];
  for (const item of rawRefs) {
    if (item == null || typeof item !== 'object') continue;
    refs.push({
      id: String('id' in item ? item.id ?? '' : ''),
      account: String('account' in item ? item.account ?? '' : ''),
    });
  }
  return { refs };
}

/** 필터 옵션 (`list.facets`) */
export async function fetchInfluencerFacets(
  params: InfluencerFacetsRequest,
): Promise<InfluencerFacetsResponse> {
  const mocked = resolveMockInfluencerFacets(params);
  if (mocked) return mocked;

  const response = await requestInfluencerOp({ op: 'list.facets', params });
  const data = assertOpObject(response.data, 'fetchInfluencerFacets');
  const toList = (value: unknown): string[] => (Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []);
  return {
    genders: toList('genders' in data ? data.genders : null),
    ageRanges: toList('ageRanges' in data ? data.ageRanges : null),
  };
}

/** ID 목록 조회 (`docs.byIds`) — 입력 순서 유지 */
export async function fetchInfluencerDocsByIds(
  params: InfluencerDocsByIdsRequest,
): Promise<InfluencerDocsByIdsResponse> {
  const mocked = resolveMockInfluencerDocsByIds(params);
  if (mocked) return mocked;

  const response = await requestInfluencerOp({ op: 'docs.byIds', params });
  const data = assertOpObject(response.data, 'fetchInfluencerDocsByIds');
  return { rows: readRows(data, 'fetchInfluencerDocsByIds') };
}

/** fullList 전건 스캔 (`fullList.scan`) */
export async function fetchInfluencerFullList(
  params: InfluencerFullListRequest,
): Promise<InfluencerFullListResponse> {
  const response = await requestInfluencerOp({ op: 'fullList.scan', params });
  const data = assertOpObject(response.data, 'fetchInfluencerFullList');
  return { rows: readRows(data, 'fetchInfluencerFullList') };
}

/**
 * 계정 쌍 묶음 → **그중 소형 인플루언서만** (`accounts.micro`).
 * ⚠️ 색인에 없는 계정·팔로워 구간 밖 계정은 빠져서 온다 — 개수가 줄어드는 것이 정상이다.
 */
export async function fetchInfluencerMicroAccounts(
  params: InfluencerMicroAccountsRequest,
): Promise<InfluencerMicroAccountsResponse> {
  const response = await requestInfluencerOp({ op: 'accounts.micro', params });
  const data = assertOpObject(response.data, 'fetchInfluencerMicroAccounts');
  return { rows: readRows(data, 'fetchInfluencerMicroAccounts') };
}

/** 최신 발굴 주차 (`week.latest`) */
export async function fetchInfluencerLatestWeek(
  params: InfluencerLatestWeekRequest,
): Promise<InfluencerLatestWeekResponse> {
  const mocked = resolveMockInfluencerLatestWeek(params);
  if (mocked) return mocked;

  const response = await requestInfluencerOp({ op: 'week.latest', params });
  const data = assertOpObject(response.data, 'fetchInfluencerLatestWeek');
  const raw = 'weekId' in data ? data.weekId : null;
  return { weekId: typeof raw === 'string' && raw.trim() ? raw : null };
}

/** 주간 신규 breakdown (`week.newBreakdown`) — 라벨은 호출부가 붙인다 */
export async function fetchInfluencerWeekBreakdown(
  params: InfluencerWeekBreakdownRequest,
): Promise<InfluencerWeekBreakdownResponse> {
  const mocked = resolveMockInfluencerWeekBreakdown(params);
  if (mocked) return mocked;

  const response = await requestInfluencerOp({ op: 'week.newBreakdown', params });
  const data = assertOpObject(response.data, 'fetchInfluencerWeekBreakdown');

  const toRows = (value: unknown) => {
    const out: { key: string; count: number }[] = [];
    for (const item of Array.isArray(value) ? value : []) {
      if (item == null || typeof item !== 'object') continue;
      const key = String('key' in item ? item.key ?? '' : '').trim();
      const count = readCount('count' in item ? item.count : null);
      if (key) out.push({ key, count });
    }
    return out;
  };

  const tierKeys = ['micro', 'mid', 'macro', 'mega'] as const;
  const followerRows = toRows('followerRows' in data ? data.followerRows : null)
    .flatMap((row) => {
      const key = tierKeys.find((name) => name === row.key);
      return key ? [{ key, count: row.count }] : [];
    });

  const rawLatest = 'latestIngestedMs' in data ? data.latestIngestedMs : null;

  return {
    total: readCount('total' in data ? data.total : null),
    channelRows: toRows('channelRows' in data ? data.channelRows : null),
    marketRows: toRows('marketRows' in data ? data.marketRows : null),
    followerRows,
    latestIngestedMs: typeof rawLatest === 'number' && Number.isFinite(rawLatest)
      ? rawLatest
      : null,
  };
}

/** 문서 한 건 (`doc.get`) */
export async function fetchInfluencerDoc(
  params: InfluencerDocGetRequest,
): Promise<InfluencerDocGetResponse> {
  const mocked = resolveMockInfluencerDoc(params);
  if (mocked) return mocked;

  const response = await requestInfluencerOp({ op: 'doc.get', params });
  const data = assertOpObject(response.data, 'fetchInfluencerDoc');
  return { row: 'row' in data ? data.row ?? null : null };
}

/** 제외 표시 동기화 (`doc.markExcluded`) — 이 유선의 첫 쓰기 질문 */
export async function markInfluencerExcluded(
  params: InfluencerMarkExcludedRequest,
): Promise<InfluencerMarkExcludedResponse> {
  const response = await requestInfluencerOp({ op: 'doc.markExcluded', params });
  const data = assertOpObject(response.data, 'markInfluencerExcluded');
  return { ok: ('ok' in data ? data.ok : null) === true };
}
